import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import AppSelect from 'src/components/common/AppSelect';
import { AsyncSingleSelectDropdown } from 'src/components/common/AsyncSingleSelectDropdown';
import { OrganizationService } from 'src/services/api/OrganizationService';
import { BillingService } from 'src/services/api/BillingService';
import { useJoinedCandidateInvoices } from 'src/services/api/queries/billingQueries';
import { cn, formatDate } from 'src/lib/utils';
import { getInitials, paletteForSeed } from 'src/lib/avatar';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from 'src/components/ui/tooltip';

const FieldLabel = ({ htmlFor, children, required }) => (
  <label
    htmlFor={htmlFor}
    className="block text-[12.5px] font-[560] text-foreground mb-1.5 tracking-[.005em]"
  >
    {children}
    {required && <span className="text-destructive ml-0.5">*</span>}
  </label>
);

const fieldBase =
  'w-full h-9 px-3 py-2 rounded-lg border border-input bg-card text-sm text-foreground-strong placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] disabled:opacity-50 disabled:cursor-not-allowed';

// Organisation dropdown row — there's no logo field anywhere in this app's
// organization model (confirmed against Organizations.jsx's own columns,
// the same listAllOrganizations API this dropdown's fetcher calls), so a
// colored initials avatar stands in for one — same deterministic
// paletteForSeed/getInitials helper CandidateCell.jsx and the interviewer
// picker already use, rather than a one-off treatment just for this row.
// Metadata line is customer_type + description, the two fields
// Organizations.jsx itself surfaces for an org beyond its name.
const OrgOption = ({ org }) => {
  const { bg, text } = paletteForSeed(org.name || String(org.org_id || ''));
  const metaLine = [org.customer_type, org.description]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="flex items-center gap-2.5 py-0.5 min-w-0 w-full">
      <span
        className={cn(
          'flex items-center justify-center w-8 h-8 rounded-full text-[11px] font-semibold shrink-0',
          bg,
          text
        )}
      >
        {getInitials(org.name)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium text-foreground-strong truncate">
          {org.name || 'Unknown'}
        </div>
        {metaLine && (
          <div className="text-[11px] text-muted-foreground truncate">
            {metaLine}
          </div>
        )}
      </div>
      {org.is_active === false && (
        <span className="text-[9.5px] font-[700] uppercase tracking-[0.05em] px-[6px] py-px rounded bg-muted text-muted-foreground shrink-0">
          Inactive
        </span>
      )}
    </div>
  );
};

// Candidate dropdown row — same avatar-standing-in-for-a-photo treatment as
// OrgOption above. Metadata line is cost-per-candidate / CTC / joining date
// — the figures actually relevant to deciding who to credit — formatted the
// same way CreditNotesInfo.jsx already formats these two amounts
// (toLocaleString(), no currency-conversion logic). joining_date goes
// through this app's shared formatDate rather than the API's raw
// yyyy-mm-dd. The trailing pill surfaces creditable_invoice_count up front,
// so "nothing to credit" is visible before opening the invoices lookup for
// that candidate.
const CandidateOption = ({ candidate: c }) => {
  const name = c.candidate_name || c.name || 'Unknown';
  const { bg, text } = paletteForSeed(
    name || String(c.candidate_joining_details_id)
  );
  const metaLine = [
    c.cost_per_candidate != null
      ? `Cost ₹${c.cost_per_candidate.toLocaleString()}`
      : null,
    c.ctc_amount != null ? `CTC ₹${c.ctc_amount.toLocaleString()}` : null,
    formatDate(c.joining_date, ''),
  ]
    .filter(Boolean)
    .join(' · ');
  const creditableCount = c.creditable_invoice_count || 0;
  return (
    <div className="flex items-center gap-2.5 py-0.5 min-w-0 w-full">
      <span
        className={cn(
          'flex items-center justify-center w-8 h-8 rounded-full text-[11px] font-semibold shrink-0',
          bg,
          text
        )}
      >
        {getInitials(name)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium text-foreground-strong truncate">
          {name}
        </div>
        {metaLine && (
          <div className="text-[11px] text-muted-foreground truncate">
            {metaLine}
          </div>
        )}
      </div>
      <span
        className={cn(
          'text-[9.5px] font-[700] uppercase tracking-[0.05em] px-[6px] py-px rounded shrink-0',
          creditableCount > 0
            ? 'bg-ds-success-subtle text-ds-success-deep'
            : 'bg-muted text-muted-foreground'
        )}
      >
        {creditableCount > 0 ? `${creditableCount} creditable` : 'None'}
      </span>
    </div>
  );
};

// Backend's CreditAdjustmentReason enum — the actual classification this
// credit note is being issued for. Distinct from the free-text field below
// (payload key `details`): `reason` is this enum code, `details` is
// whatever extra context the user types.
const REASON_OPTIONS = [
  { value: 'CANDIDATE_EXIT_EARLY', label: 'Candidate Exit (Early)' },
  { value: 'CANDIDATE_REPLACEMENT', label: 'Candidate Replacement' },
  { value: 'BILLING_ERROR', label: 'Billing Error' },
  { value: 'OTHER', label: 'Other' },
];

// These two reasons only make sense once a specific candidate is attached —
// hidden from the options list until one's picked (per explicit
// instruction). Billing Error / Other stay available either way, since a
// credit note can be posted against just an organisation (no candidate at
// all — also per explicit instruction, see isFormValid below).
const CANDIDATE_ONLY_REASONS = new Set([
  'CANDIDATE_EXIT_EARLY',
  'CANDIDATE_REPLACEMENT',
]);

const defaultFormData = {
  organization: null, // { label, value: org_id, item } from AsyncSingleSelectDropdown
  candidate: null, // { label, value: candidate_joining_details_id, item }
  reasonCode: '', // CreditAdjustmentReason enum value
  amount: '',
  taxPercent: '',
  details: '',
};

// Org list has no server-side search param (confirmed against
// OrganizationService.listAllOrganizations/endpoints.jsx), so — same pattern
// JobPostingsModal.jsx's own fetchLocations already uses for the same
// reason — fetch a page and filter it client-side by the typed search term.
const useOrganizationFetcher = () =>
  useCallback(async ({ page, pageSize, search }) => {
    try {
      const resolvedPageSize = page > 1 ? 50 : pageSize;
      const response = await OrganizationService.listAllOrganizations(
        page,
        resolvedPageSize
      );
      const organizations = response?.data?.items || [];
      let filtered = organizations;
      if (search && search.trim()) {
        const searchLower = search.toLowerCase();
        filtered = organizations.filter((org) =>
          (org.name || '').toLowerCase().includes(searchLower)
        );
      }
      return {
        items: filtered,
        hasNext: organizations.length >= resolvedPageSize,
      };
    } catch (err) {
      return { items: [], hasNext: false };
    }
  }, []);

// GET .../credit-notes/organizations/:orgId/joined-candidates — real search
// is server-side here (unlike organizations above), so just pass it through.
// Confirmed response shape (real sample): data.items[] with
// candidate_joining_details_id/candidate_name/creditable_invoice_count etc.,
// data.pagination.has_next.
const useJoinedCandidatesFetcher = (orgId) =>
  useCallback(
    async ({ page, pageSize, search }) => {
      if (!orgId) return { items: [], hasNext: false };
      try {
        const response = await BillingService.getJoinedCandidatesByOrganization(
          { orgId, search, page, pageSize }
        );
        return {
          items: response?.data?.items || [],
          hasNext: !!response?.data?.pagination?.has_next,
        };
      } catch (err) {
        return { items: [], hasNext: false };
      }
    },
    [orgId]
  );

// Confirmed real invoice shape (sample response): each invoice carries its
// own candidate_amount/candidate_tax_amount/candidate_total_amount — "the
// share billed to the candidate" — and creditable_amount/
// creditable_tax_amount/creditable_total_amount — "how much can still be
// credited" — both per the original endpoint description. There's no
// top-level credited_amount/remaining_creditable_amount when invoices[] is
// non-empty (that shape only showed up once, for a candidate with zero
// creditable invoices) — so every figure here is summed across invoices
// instead. is_creditable defensively filters out any invoice the backend
// might include as informational-only.
const sumInvoiceField = (invoiceList, field) =>
  invoiceList.reduce((total, inv) => total + (Number(inv[field]) || 0), 0);

// All of a candidate's invoices should normally share one GST rate; if they
// don't (e.g. an older invoice at a different rate), average weighted by
// each invoice's own candidate_amount rather than just taking the first
// one arbitrarily.
const weightedTaxRatePercent = (invoiceList, totalAmount) => {
  if (invoiceList.length === 0) return null;
  const rates = invoiceList.map((inv) => Number(inv.tax_rate_percent) || 0);
  if (rates.every((r) => r === rates[0])) return rates[0];
  if (!totalAmount) return rates[0];
  const weighted = invoiceList.reduce(
    (sum, inv) =>
      sum +
      (Number(inv.tax_rate_percent) || 0) * (Number(inv.candidate_amount) || 0),
    0
  );
  return weighted / totalAmount;
};

export const CreateCreditNoteModal = ({ onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState(defaultFormData);
  // Errors only render once a submit has actually been attempted — see
  // handleSubmit — so the form doesn't greet the user with a wall of red
  // before they've touched anything, but still gives a clear reason the
  // moment they try to submit an incomplete form.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const fetchOrganizations = useOrganizationFetcher();
  const fetchJoinedCandidates = useJoinedCandidatesFetcher(
    formData.organization?.value
  );

  // Fires once a candidate (really: their candidate_joining_details_id) is
  // picked.
  // Confirmed response shape — joining_details, plus invoices[] each
  // carrying their own candidate_amount/candidate_total_amount ("billed to
  // candidate") and creditable_amount/creditable_total_amount ("still
  // creditable"). is_creditable defensively excludes any invoice the
  // backend might include as informational-only.
  const {
    data: invoicesResponse,
    isFetching: isLoadingInvoices,
    isError: invoicesError,
  } = useJoinedCandidateInvoices(formData.candidate?.value);
  const joiningDetails = invoicesResponse?.data?.joining_details ?? null;
  const billedOrgId = invoicesResponse?.data?.billed_organization?.org_id;
  const allInvoices = invoicesResponse?.data?.invoices ?? [];
  const creditableInvoices = allInvoices.filter(
    (inv) => inv.is_creditable !== false
  );
  const hasLoadedInvoices = !!invoicesResponse && !isLoadingInvoices;
  const nothingCreditable =
    hasLoadedInvoices && creditableInvoices.length === 0;

  const totalCandidateAmount = sumInvoiceField(
    creditableInvoices,
    'candidate_amount'
  );
  const totalCandidateBilled = sumInvoiceField(
    creditableInvoices,
    'candidate_total_amount'
  );
  const totalCreditableTotal = sumInvoiceField(
    creditableInvoices,
    'creditable_total_amount'
  );
  const taxRatePercent = weightedTaxRatePercent(
    creditableInvoices,
    totalCandidateAmount
  );
  const creditableInvoiceNumbers = creditableInvoices
    .map((inv) => inv.invoice_number)
    .filter(Boolean);
  const extraInvoiceNumbers = creditableInvoiceNumbers.slice(1);

  // Amount prefills from the sum of every creditable invoice's own
  // candidate_amount (per explicit instruction); Tax % prefills from the
  // invoices' own tax_rate_percent. Both stay editable afterward.
  useEffect(() => {
    if (creditableInvoices.length === 0) return;
    setFormData((prev) => ({
      ...prev,
      amount: String(totalCandidateAmount),
      taxPercent:
        taxRatePercent != null ? String(taxRatePercent) : prev.taxPercent,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCandidateAmount, taxRatePercent]);

  const set = (key) => (e) =>
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  const handleOrganizationChange = (option) => {
    setFormData({ ...defaultFormData, organization: option });
    setSubmitAttempted(false);
  };

  const handleCandidateChange = (option) => {
    setFormData((prev) => ({
      ...defaultFormData,
      organization: prev.organization,
      candidate: option,
    }));
    setSubmitAttempted(false);
  };

  const handleAmountChange = (e) =>
    setFormData((prev) => ({ ...prev, amount: e.target.value }));

  const handleTaxPercentChange = (e) =>
    setFormData((prev) => ({ ...prev, taxPercent: e.target.value }));

  // Final Amount is never directly editable — always Amount + (Amount ×
  // Tax% / 100), per explicit instruction.
  const finalAmount = (() => {
    const a = Number(formData.amount);
    const t = Number(formData.taxPercent);
    if (formData.amount === '' || Number.isNaN(a) || Number.isNaN(t))
      return null;
    return a + (a * t) / 100;
  })();

  const exceedsCreditable =
    hasLoadedInvoices &&
    finalAmount != null &&
    finalAmount > totalCreditableTotal;

  // Candidate is optional — per explicit instruction, a credit note can be
  // posted against just an organisation (amount + tax, no candidate/
  // invoices attached at all). The invoice-creditable-ceiling check below
  // only applies when there IS a candidate (nothingCreditable/
  // exceedsCreditable both stay false with none selected, since they're
  // derived off invoices that only ever load once a candidate is picked).
  const fieldErrors = {
    organization: !formData.organization ? 'Select a customer.' : '',
    reasonCode: !formData.reasonCode ? 'Select a reason.' : '',
    amount:
      // eslint-disable-next-line no-nested-ternary
      formData.amount === ''
        ? 'Amount is required.'
        : finalAmount == null
          ? 'Enter a valid amount.'
          : '',
    details: !formData.details.trim() ? 'Details are required.' : '',
  };
  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);
  const orgIdReady = !formData.candidate || !!billedOrgId;
  const isFormValid =
    !hasFieldErrors && !nothingCreditable && !exceedsCreditable && orgIdReady;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) {
      setSubmitAttempted(true);
      return;
    }
    const amount = Number(formData.amount);
    onSubmit({
      org_id: formData.candidate ? billedOrgId : formData.organization.value,
      candidate_joining_details_id: formData.candidate?.value,
      invoice_ids: creditableInvoices.map((inv) => inv.invoice_id),
      reason: formData.reasonCode,
      details: formData.details.trim(),
      amount,
      tax_percent: Number(formData.taxPercent) || 0,
      tax_amount: finalAmount - amount,
      total_amount: finalAmount,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      {/* min-h so the Organisation dropdown's own popup (search bar +
          options list, up to ~300px) has room to render before org/candidate
          are picked — the section below only grows the form AFTER that, so
          without a floor here the body is just one field tall and this
          overflow-y-auto clips the popup instead of letting it show. The
          dropdown itself is a plain absolutely-positioned popup, not a
          portal, so it's bound by whatever ancestor it renders inside. */}
      <div className="flex-1 overflow-y-auto px-[22px] py-[18px] space-y-4 min-h-[420px]">
        <div>
          <FieldLabel htmlFor="credit-note-organization" required>
            Customer
          </FieldLabel>
          <AsyncSingleSelectDropdown
            fetcher={fetchOrganizations}
            getOptionLabel={(org) => org.name || 'Unknown'}
            getOptionValue={(org) => org.org_id}
            value={formData.organization}
            onChange={handleOrganizationChange}
            placeholder="Select customer"
            searchPlaceholder="Search customers..."
            valueDisplay="text"
            showSelectionIndicator={false}
            renderOption={(option) => <OrgOption org={option.item} />}
          />
          {submitAttempted && fieldErrors.organization && (
            <p className="text-[11px] text-destructive mt-1">
              {fieldErrors.organization}
            </p>
          )}
        </div>

        {formData.organization && (
          <>
            <div>
              <FieldLabel htmlFor="credit-note-candidate">
                Candidate (optional)
              </FieldLabel>
              <AsyncSingleSelectDropdown
                fetcher={fetchJoinedCandidates}
                getOptionLabel={(c) => c.candidate_name || c.name || 'Unknown'}
                getOptionValue={(c) => c.candidate_joining_details_id}
                value={formData.candidate}
                onChange={handleCandidateChange}
                placeholder="Select candidate"
                searchPlaceholder="Search candidates..."
                valueDisplay="text"
                showSelectionIndicator={false}
                renderOption={(option) => (
                  <CandidateOption candidate={option.item} />
                )}
                refetchOnOpen
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Candidates with verified joining under this customer. Leave
                blank to post a credit note against the customer alone.
              </p>
            </div>

            <div>
              <FieldLabel htmlFor="credit-note-reason" required>
                Reason
              </FieldLabel>
              <AppSelect
                id="credit-note-reason"
                options={
                  formData.candidate
                    ? REASON_OPTIONS
                    : REASON_OPTIONS.filter(
                        (opt) => !CANDIDATE_ONLY_REASONS.has(opt.value)
                      )
                }
                value={formData.reasonCode}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, reasonCode: value }))
                }
                placeholder="Select reason"
                disabled={loading}
              />
              {submitAttempted && fieldErrors.reasonCode && (
                <p className="text-[11px] text-destructive mt-1">
                  {fieldErrors.reasonCode}
                </p>
              )}
            </div>

            {formData.candidate && isLoadingInvoices && (
              <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground py-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading invoices…
              </div>
            )}

            {formData.candidate && invoicesError && (
              <p className="text-[12.5px] text-destructive">
                Failed to load this candidate&apos;s invoices.
              </p>
            )}

            {nothingCreditable && (
              <p className="text-[12.5px] text-muted-foreground bg-muted rounded-lg px-3 py-2">
                No creditable invoices for this candidate — nothing left to
                credit against their joining.
              </p>
            )}

            {hasLoadedInvoices && !nothingCreditable && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-muted-foreground bg-muted rounded-lg px-3 py-2">
                {creditableInvoiceNumbers.length > 0 && (
                  <span className="inline-flex items-center gap-1">
                    Invoice{creditableInvoiceNumbers.length > 1 ? 's' : ''}:{' '}
                    <b className="text-foreground-strong font-semibold">
                      {creditableInvoiceNumbers[0]}
                    </b>
                    {extraInvoiceNumbers.length > 0 && (
                      <TooltipProvider>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center justify-center text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-border text-foreground-strong cursor-default">
                              +{extraInvoiceNumbers.length}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {extraInvoiceNumbers.join(', ')}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </span>
                )}
                <span>
                  Billed to candidate:{' '}
                  <b className="text-foreground-strong font-semibold">
                    ₹{totalCandidateBilled.toLocaleString()}
                  </b>
                </span>
                <span>
                  Still creditable:{' '}
                  <b className="text-foreground-strong font-semibold">
                    ₹{totalCreditableTotal.toLocaleString()}
                  </b>
                </span>
              </div>
            )}

            {joiningDetails?.joining_date && (
              <p className="text-[11px] text-muted-foreground -mt-2">
                Joined {joiningDetails.joining_date}
                {joiningDetails.requirement_title
                  ? ` · ${joiningDetails.requirement_title}`
                  : ''}
                {joiningDetails.po_number
                  ? ` · PO ${joiningDetails.po_number}`
                  : ''}
              </p>
            )}

            {!nothingCreditable && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel htmlFor="credit-note-amount" required>
                      Amount
                    </FieldLabel>
                    <input
                      id="credit-note-amount"
                      type="number"
                      step="0.01"
                      className={cn(
                        fieldBase,
                        submitAttempted &&
                          fieldErrors.amount &&
                          'border-destructive focus:border-destructive focus:ring-destructive/10'
                      )}
                      value={formData.amount}
                      onChange={handleAmountChange}
                      placeholder="0.00"
                      disabled={loading}
                    />
                    {submitAttempted && fieldErrors.amount && (
                      <p className="text-[11px] text-destructive mt-1">
                        {fieldErrors.amount}
                      </p>
                    )}
                  </div>
                  <div>
                    <FieldLabel htmlFor="credit-note-tax">Tax %</FieldLabel>
                    <input
                      id="credit-note-tax"
                      type="number"
                      step="0.01"
                      className={fieldBase}
                      value={formData.taxPercent}
                      onChange={handleTaxPercentChange}
                      placeholder="e.g. 18"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <FieldLabel>Final Amount</FieldLabel>
                  {/* Never directly editable — always Amount + (Amount ×
                      Tax% / 100). A styled read-only div rather than a
                      disabled <input> so it doesn't look like a field
                      waiting for input. */}
                  <div
                    className={cn(
                      'h-9 px-3 flex items-center rounded-lg border bg-muted text-sm font-semibold text-foreground-strong',
                      exceedsCreditable ? 'border-destructive' : 'border-input'
                    )}
                  >
                    {finalAmount != null
                      ? `₹${finalAmount.toLocaleString()}`
                      : '—'}
                  </div>
                  {exceedsCreditable && (
                    <p className="text-[11px] text-destructive mt-1">
                      Exceeds the ₹{totalCreditableTotal.toLocaleString()} still
                      creditable for this candidate.
                    </p>
                  )}
                </div>

                <div>
                  <FieldLabel htmlFor="credit-note-details" required>
                    Details
                  </FieldLabel>
                  <textarea
                    id="credit-note-details"
                    className={cn(
                      fieldBase,
                      'resize-y min-h-[72px] leading-[1.65]',
                      submitAttempted &&
                        fieldErrors.details &&
                        'border-destructive focus:border-destructive focus:ring-destructive/10'
                    )}
                    value={formData.details}
                    onChange={set('details')}
                    placeholder="Explain the reason for this credit note..."
                    disabled={loading}
                  />
                  {submitAttempted && fieldErrors.details && (
                    <p className="text-[11px] text-destructive mt-1">
                      {fieldErrors.details}
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="flex justify-end gap-2 px-[22px] py-[14px] border-t border-border bg-brand-bg-subtle shrink-0">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
        {/* Stays clickable even while invalid (loading aside) so a click
            always runs handleSubmit — that's what flips submitAttempted and
            surfaces the field errors above; a pre-emptively disabled button
            would silently block the click and the user would never see
            why. */}
        <Button type="submit" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Creating...
            </>
          ) : (
            'Create Credit Note'
          )}
        </Button>
      </div>
    </form>
  );
};

export default CreateCreditNoteModal;
